import { ReactNode, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { ChevronDownV3Icon } from "components/icons";

import { useOutsideClick } from "hooks/useOutsideClick";
import cn from "lib/cn";

interface IDropdownItem {
  label: string;
  icon?: ReactNode;
  href?: string;
  onClick?: () => void;
  disabled?: boolean;
  active?: boolean;
}

interface IDropdownProps {
  trigger: ReactNode;
  items: IDropdownItem[];
  disabled?: boolean;
  className?: string;
  active?: boolean;
  triggerClassName?: string;
  dropdownClassName?: string;
}

const Dropdown = ({
  trigger,
  items,
  disabled,
  className,
  active,
  triggerClassName,
  dropdownClassName,
}: IDropdownProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  useOutsideClick(ref, () => setOpen(false));

  const handleItemClick = (item: IDropdownItem) => {
    if (item.disabled) return;
    setOpen(false);

    if (item.onClick) item.onClick();
    if (item.href) window.location.href = item.href;
  };

  const handleToggleDropdown = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  useEffect(() => {
    if (!triggerRef.current) return;

    const updatePosition = () => {
      const rect = triggerRef.current!.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY - 10,
        left: rect.left + window.scrollX,
      });
    };

    if (open) {
      updatePosition();
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition, true);
    }

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open]);

  return (
    <div ref={ref} className={cn("relative h-full flex flex-col", className)}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        className={cn(
          "group relative flex items-center gap-2 h-full text-base text-gray-900",
          disabled
            ? "cursor-not-allowed"
            : "focus:text-primary-500 focus:font-semibold",
          active && "text-primary-500 font-semibold",
          triggerClassName
        )}
        onClick={handleToggleDropdown}
      >
        {trigger}

        <ChevronDownV3Icon
          width={12}
          height={12}
          className={cn(
            "text-gray-900 transition-colors group-focus:text-primary-500 group-focus:font-semibold",
            active && "text-primary-500"
          )}
        />
      </button>

      {open &&
        !disabled &&
        createPortal(
          <div
            className={cn(
              "fixed z-50 w-max min-w-[14rem] rounded-md border-gray-100 border bg-white drop-shadow-md",
              dropdownClassName
            )}
            style={{ top: position.top, left: position.left }}
          >
            {items.map((item, idx) => (
              <div
                key={idx}
                className={cn(
                  "flex items-center p-3 h-14 border-b-gray-100 border-b hover:text-primary-500 last:border-b-0",
                  item.disabled ? "cursor-not-allowed" : "cursor-pointer",
                  item.active && "bg-white text-primary-500"
                )}
                onClick={() => handleItemClick(item)}
              >
                {item.icon && <span className="shrink-0">{item.icon}</span>}
                <span>{item.label}</span>
              </div>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
};

export default Dropdown;
